import { TbFileDownload } from "react-icons/tb";
import React, { useEffect, useState, useRef } from "react";
import * as XLSX from "xlsx";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import useApi from "../../hooks/useApi";
import { flagMap, returnReasons } from "./data";
import Modal from "../common/CommonModal";
import TransferOrderModal from "./TransferOrderModal";
import { AlertCircle, FileSpreadsheet, ListCheck, Upload } from "lucide-react";
import canUserSeeThis from "../common/CheckCanUserSeeThis";
import TransferInvoiceListModal from "../salesReturn/TransferInvoiceListModal";
import ExcelImportModal from "./ExcelImportModal";

const ConfigCard = ({
  invoiceData,
  setInvoiceData,
  autoVatIsOn,
  setAutoVatIsOn,
  invoiceNoFromState,
  accountData,
  onTransfer,
  isModalOpen,
  setIsModalOpen,
  documentType,
  handleTransfer,
  transferInvNo,
  setTransferInvNo,
  fetchDocumentDetail,
  onPickItems,
  onImportExcel,
  config,
  showCurrency = true,
  showVatAutoCalculation = true,
  glSettings
}) => {
  const COMPANY_CODE = localStorage?.getItem("companyCode") || "-";
  const API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const BASE_URL = API_PATHS[0].API_CONFIG || "https://cubixweberp.com:213";
  const DEPARTMENT =
    localStorage.getItem("currentSelectedDepartment") || "MAIN";
  const [docTransfer, setDocTransfer] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedDocType, setSelectedDocType] = useState("");
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [modalData, setModalData] = useState([]);
  const [modalSearchTerm, setModalSearchTerm] = useState("");

  // Excel upload refs / state (initialised to avoid undefined errors)
  const fileInputRef = useRef(null);
  const [excelData, setExcelData] = useState([]);
  const [excelFileName, setExcelFileName] = useState("");
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [excelError, setExcelError] = useState(null);
  const [showExcelModal, setShowExcelModal] = useState(false);

  // Handle file selection and parsing
  const handleFileChange = (e) => {
    const file = e?.target?.files && e.target.files[0];
    if (!file) return;
    setExcelFileName(file.name);
    setExcelError(null);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
        setExcelData(json);
      } catch (err) {
        console.error("Failed to parse Excel:", err);
        setExcelError("Failed to parse file. Ensure a valid Excel file.");
        setExcelData([]);
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleImport = async () => {
    if (!excelData || excelData.length === 0) {
      setExcelError("No data to import");
      return;
    }

    setLoadingExcel(true);
    setExcelError(null);

    try {
      // 1. Prepare data (Ensure row.code matches your Excel header name)
      const checkPayload = excelData.map((row) => ({
        cmpcode: COMPANY_CODE,
        code: String(row.code || row.Code || "").trim(),
      }));

      // 2. Call the StockCheck API
      const response = await fetch(`${BASE_URL}/api/StockCheck`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(checkPayload),
      });

      if (!response.ok)
        throw new Error("Failed to verify items with the server.");

      const data = await response.json();

      // 3. Check the specific 'notMatchingItems' property from your response
      if (data.notMatchingItems && data.notMatchingItems.length > 0) {
        const missingCodes = data.notMatchingItems
          .map((item) => item.code)
          .join(", ");

        const proceed = window.confirm(
          `The following items are not in stock or not found:\n${missingCodes}\n\nDo you want to skip these and import the rest?`
        );

        if (proceed) {
          const missingSet = new Set(
            data.notMatchingItems.map((item) => String(item.code).trim().toLowerCase())
          );

          const validExcelData = excelData.filter((row) => {
            const code = String(row.code || row.Code || "").trim().toLowerCase();
            return !missingSet.has(code);
          });

          if (validExcelData.length === 0) {
            setExcelError("No valid items left to import.");
            setLoadingExcel(false);
            return;
          }

          await onImportExcel(validExcelData);
        } else {
          setExcelError(`The following items are not in stock: ${missingCodes}`);
          setLoadingExcel(false);
          return; // Halt the process here
        }
      } else {
        // 4. If result is "Success" and no missing items, proceed
        await onImportExcel(excelData);
      }

      setExcelData([]);
      setExcelFileName("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      setShowExcelModal(false);
    } catch (err) {
      console.error("Import/Validation failed:", err);
      setExcelError(err?.message || "An error occurred during verification.");
    } finally {
      setLoadingExcel(false);
    }
  };

  const { request, loading: loadingModalData } = useApi();
  const fetchModalData = async () => {
    const query = modalSearchTerm.trim() === "" ? "-" : modalSearchTerm;

    const customerAccount =
      (typeof accountData === "string"
        ? accountData
        : accountData?.account || accountData?.ACCOUNT) ||
      "-";

    const finalAccount =
      customerAccount === "-" || customerAccount === "00000000"
        ? glSettings?.find((g) => g.Scrn_code === "SI-CA-D")?.ACCOUNT || ""
        : customerAccount;

    const url = `${BASE_URL}/api/CRMDocTransfer/${COMPANY_CODE}/LIST/${selectedDocType}/-/${finalAccount}/${DEPARTMENT}/${query}/-`;

    console.log("MASTER URL for transfer master", url);

    try {
      const response = await request({
        url,
        method: "GET",
        timeout: 10000,
      });

      console.log("Response", response);
      setModalData(response || []);
    } catch (err) {
      console.error("Error loading data:", err);
      setModalData([]);
    }
  };

  const handleOpenModal = async () => {
    if (
      (documentType === "SALESRETURN" || documentType === "PURCHASERETURN") &&
      invoiceData.paymentType === "credit" &&
      !accountData?.account
    ) {
      alert("Please select a customer before transferring.");
      return;
    }

    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const excelFormatTooltip = (
    <div style={{ fontSize: "0.85rem", maxWidth: 200 }}>
      <strong>Download Excel Format:</strong>
      <div style={{ margin: "4px 0 0 0", padding: 0, display: "flex" }}>
        <p>Code,Desc,Qty,Price,Unit,Group</p>
      </div>
    </div>
  );
  const { request: requestDocTransfer } = useApi();

  const fetchDocTransfer = async () => {
    const url = `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/DOCTRANSFER/-/${documentType}`;
    try {
      const response = await requestDocTransfer({
        url: url,
        method: "GET",
        timeout: 10000,
      });
      console.log("Response", response);
      let fetchedDocs = response || [];
      // Ensure IMPXL is available for all documents that use this config card
      if (!fetchedDocs.some((doc) => doc.TransferDocID === "IMPXL")) {
        fetchedDocs = [
          ...fetchedDocs,
          { TransferDocID: "IMPXL", TransferDocName: "Import Excel" },
        ];
      }
      setDocTransfer(fetchedDocs);
    } catch (err) {
      console.error("Error loading data:", err);
      // Fallback to ensuring IMPXL is available
      setDocTransfer([
        { TransferDocID: "IMPXL", TransferDocName: "Import Excel" },
      ]);
    }
  };

  const { request: requestCurrencyList } = useApi();
  const fetchCurrencyList = async () => {
    const url = `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/CURRENCY/-/-`;
    try {
      const response = await requestCurrencyList({
        url: url,
        method: "GET",
        timeout: 10000,
      });

      console.log("Response", response);
      setCurrencies(response || []);

      if (!invoiceNoFromState && response && response.length > 0) {
        const defaultCurrency = response[0];
        setSelectedCurrency(defaultCurrency);
      }
    } catch (err) {
      console.error("Error loading data:", err);
      setCurrencies([]);
    }
  };

  useEffect(() => {
    fetchDocTransfer();
    fetchCurrencyList();
  }, [invoiceNoFromState]);

  useEffect(() => {
    if (
      invoiceData.currency &&
      currencies.length > 0 &&
      invoiceNoFromState // only when loading from existing invoice
    ) {
      const matchedCurrency = currencies.find(
        (c) => c.curno === invoiceData.currency,
      );

      if (matchedCurrency) {
        setSelectedCurrency(matchedCurrency);
      }
    }
  }, [invoiceData.currency, currencies, invoiceNoFromState]);

  useEffect(() => {
    if (selectedCurrency) {
      setInvoiceData((prev) => ({
        ...prev,
        currency: selectedCurrency.curno,
        currencyRate: selectedCurrency.rate || 1,
      }));
    }
  }, [selectedCurrency]);

  const handleChange = (event) => {
    const selected =
      currencies.find((c) => c.curno === event.target.value) || null;
    setSelectedCurrency(selected);
    setInvoiceData((prev) => ({
      ...prev,
      currency: selected?.curno || "",
      currencyRate: selected?.rate || 1,
    }));
  };
  const getOrderId = (order = {}) => {
    return (
      order.QUOTNO ||
      order.ORDNO ||
      order.SONO ||
      order.PONO ||
      order.DONO ||
      order.ISSUE_NO ||
      order.lead_id ||
      order.id || // fallback if given
      null
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrders(modalData.map(getOrderId).filter(Boolean));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOrder = (orderId) => {
    if (!orderId) return; // skip bad rows
    console.log("Toggling order ID:", orderId);
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId],
    );
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "F2") {
        // Only handle F2 if the transfer input is focused
        const isTransferInputFocused =
          document.activeElement?.getAttribute("data-input-type") ===
          "transfer";

        if (isTransferInputFocused) {
          e.preventDefault();
          setIsModalOpen(true);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [setIsModalOpen]);

  const handleTransferAction = (...args) => {
    // Log incoming args for clarity (what the Transfer modal passed)
    console.log("[ConfigCard] handleTransferAction called with args:", args);

    onTransfer(...args);

    // Only apply on SALES_INVOICE
    if (documentType === "SALESINVOICE" && selectedDocType) {
      const newJv = selectedDocType;
      console.log(
        `[ConfigCard] Preparing to set jv_num to "${newJv}" (documentType=${documentType})`,
      );
      // Set jv_num using the deterministic variable
      setInvoiceData((prev) => ({
        ...prev,
        jv_num: newJv,
      }));

      // Immediate clear, accurate log using newJv (not reading state immediately)
      console.log(
        `[ConfigCard] Setting jv_num -> "${newJv}" (documentType=${documentType})`,
      );
    }
  };
  useEffect(() => {
    if (documentType === "SALES_INVOICE") {
      console.log(
        "[ConfigCard] invoiceData.jv_num changed:",
        invoiceData?.jv_num,
      );
    }
  }, [invoiceData?.jv_num, documentType]);

  return (
    <div className=" flex flex-col gap-2">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 ">
        <div className="p-4">
          <h3 className="text-lg font-semibold text-blue-600 mb-4">
            Configuration
          </h3>
          <div className="flex flex-col gap-4">
            {showCurrency && (
              <div className="flex items-center gap-3">
                <FormControl size="small" className="flex-1">
                  <InputLabel>Foreign Currency</InputLabel>
                  <Select
                    value={selectedCurrency?.curno || ""}
                    onChange={handleChange}
                    label="Foreign Currency"
                    renderValue={(value) => {
                      const selected = currencies?.find(
                        (c) => c.curno === value,
                      );
                      return (
                        <div className="flex items-center gap-2">
                          <img
                            src={
                              flagMap[selected?.curno] ||
                              "https://flagcdn.com/w20/un.png"
                            }
                            alt={selected?.curno}
                            width={20}
                            height={15}
                            className="rounded-sm"
                          />
                          <span>
                            {selected?.curno} - {selected?.cur}
                          </span>
                        </div>
                      );
                    }}
                  >
                    {currencies?.map((currency) => (
                      <MenuItem key={currency.curno} value={currency.curno}>
                        <div className="flex justify-between items-center w-full">
                          <div className="flex items-center gap-2">
                            <img
                              src={
                                flagMap[currency?.curno] ||
                                "https://flagcdn.com/w20/un.png"
                              }
                              alt={currency.curno}
                              width={20}
                              height={15}
                              className="rounded-sm"
                            />
                            <div>
                              <div className="font-medium">
                                {currency?.curno}
                              </div>
                              <div className="text-xs text-gray-500">
                                {currency?.cur?.trim()}
                              </div>
                            </div>
                          </div>
                          <span className="text-sm font-medium text-gray-700">
                            {currency?.rate?.toFixed(3)}
                          </span>
                        </div>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Rate box */}
                <div className="px-3 py-2 bg-orange-50 border border-orange-300 rounded-md">
                  <span className="text-sm font-bold text-orange-600">
                    {selectedCurrency?.rate?.toFixed(6)}
                  </span>
                </div>
              </div>
            )}

            <hr className="border-gray-200" />

            {config.features.showTransferModule && (
              <>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <div className=" flex justify-between items-center">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {documentType === "SALESRETURN" || documentType === "PURCHASERETURN"
                          ? "Transfer Invoice"
                          : "TRF Document Type"}
                      </label>

                      {documentType === "SALESRETURN" || documentType === "PURCHASERETURN" ? (
                        <div
                          onClick={() => setIsModalOpen(true)}
                          className="lg:hidden flex text-xs underline text-gray-500"
                        >
                          Invoice List
                        </div>
                      ) : null}
                    </div>

                    {documentType === "SALESRETURN" || documentType === "PURCHASERETURN" ? (
                      <div className="flex flex-col gap-3">
                        {/* Invoice Input + Transfer Button */}
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            placeholder="Enter INV number or press (F2)"
                            value={transferInvNo}
                            data-input-type="transfer"
                            onChange={(e) => {
                              const value = e.target.value;
                              setTransferInvNo(value);
                              setInvoiceData((prev) => ({
                                ...prev,
                                inv_no: value,
                              }));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "F2") {
                                e.preventDefault();
                                setIsModalOpen(true);
                              }
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleOpenModal();
                              }
                            }}
                            className="flex-1 px-3 py-2 text-sm bg-gray-200 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />

                          <button
                            type="button"
                            onClick={handleOpenModal}
                            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-400"
                          >
                            Transfer
                          </button>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                          {/* Reason of Return */}
                          <div className="flex flex-col">
                            <label className="text-sm font-medium text-gray-700 mb-1">
                              Reason of Return
                            </label>
                            <select
                              value={invoiceData.vehicleNo || ""}
                              onChange={(e) =>
                                setInvoiceData((prev) => ({
                                  ...prev,
                                  vehicleNo: e.target.value,
                                }))
                              }
                              className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {" "}
                              <option value="">Select Reason</option>
                              {returnReasons[0]?.reasonOfReturn?.map(
                                (opt, idx) => (
                                  <option key={idx} value={opt}>
                                    {opt}
                                  </option>
                                ),
                              )}
                            </select>
                          </div>

                          {/* Condition of Part & Packing */}
                          <div className="flex flex-col">
                            <label className="text-sm font-medium text-gray-700 mb-1">
                              Condition of Part & Packing
                            </label>
                            <select
                              value={invoiceData.drivername || ""}
                              onChange={(e) =>
                                setInvoiceData((prev) => ({
                                  ...prev,
                                  drivername: e.target.value,
                                }))
                              }
                              className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select Condition</option>
                              {returnReasons[0]?.conditionOfPart?.map(
                                (opt, idx) => (
                                  <option key={idx} value={opt}>
                                    {opt}
                                  </option>
                                ),
                              )}
                            </select>
                          </div>

                          {/* Primary Error Source/Cause */}
                          <div className="flex flex-col">
                            <label className="text-sm font-medium text-gray-700 mb-1">
                              Primary Error Source / Cause
                            </label>
                            <select
                              value={invoiceData.primaryErrorField || ""}
                              onChange={(e) =>
                                setInvoiceData((prev) => ({
                                  ...prev,
                                  primaryErrorField: e.target.value,
                                }))
                              }
                              className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select Primary error</option>
                              {returnReasons[0]?.primaryError?.map(
                                (opt, idx) => (
                                  <option key={idx} value={opt}>
                                    {opt}
                                  </option>
                                ),
                              )}
                            </select>
                          </div>

                          {/* Next Action */}
                          <div className="flex flex-col">
                            <label className="text-sm font-medium text-gray-700 mb-1">
                              Next Action
                            </label>
                            <select
                              value={invoiceData.nextAction || ""}
                              onChange={(e) =>
                                setInvoiceData((prev) => ({
                                  ...prev,
                                  nextAction: e.target.value,
                                }))
                              }
                              className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select Next Action</option>
                              {returnReasons[0]?.nextAction?.map((opt, idx) => (
                                <option key={idx} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <select
                        name="deliveryOrder"
                        value={selectedDocType}
                        onChange={(e) => setSelectedDocType(e.target.value)}
                        className="w-full px-3 py-2 bg-transparent border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Document Type</option>
                        {docTransfer?.map((docType) => (
                          <option
                            key={docType.TransferDocID}
                            value={docType.TransferDocID}
                          >
                            {docType.TransferDocName}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Show only if selected type is "IMPXL" */}
                  {selectedDocType === "IMPXL" && (
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        className="text-sm p-2 bg-blue-600 hover:bg-blue-700 font-semibold flex items-center gap-1 text-white rounded-lg"
                        onClick={() => setShowExcelModal(true)}
                      >
                        Upload File
                      </button>
                    </div>
                  )}

                  {selectedDocType && selectedDocType !== "IMPXL" && (
                    <div>
                      <button
                        onClick={handleOpenModal}
                        className="text-sm p-2 capitalize  bg-blue-600 hover:bg-blue-700 font-semibold flex items-center gap-1 text-white rounded-lg"
                      >
                        {selectedDocType?.toLowerCase()} List
                        <span>
                          <ListCheck size={18} />
                        </span>
                      </button>
                    </div>
                  )}
                </div>

                {showVatAutoCalculation && (
                  <div className="grid grid-cols-3 items-end gap-2">
                    <div className="col-span-2">
                      {documentType === "SALESINVOICE" && canUserSeeThis("W1") && (
                        <div className="flex items-center gap-2 mb-2">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={invoiceData.appendDO || false}
                              onChange={(e) =>
                                setInvoiceData((prev) => ({
                                  ...prev,
                                  appendDO: e.target.checked,
                                }))
                              }
                              className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                          <span className="text-sm font-medium text-gray-700">
                            Append DO
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="min-w-[120px] col-span-1 p-2 border border-gray-200 rounded-md text-center bg-gray-50">
                      <span className="block text-xs text-gray-600">
                        VAT Auto Cal.
                      </span>
                      <div className="flex items-center justify-center gap-2 mt-1">
                        <span
                          className={`text-sm font-medium ${autoVatIsOn ? "text-green-600" : "text-gray-500"
                            }`}
                        >
                          {autoVatIsOn ? "ON" : "OFF"}
                        </span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={autoVatIsOn}
                            onChange={(e) => setAutoVatIsOn(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

              </>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        size="xxl"
        header={true}
        title={
          documentType === "SALESRETURN" || documentType === "PURCHASERETURN"
            ? "Transfer Invoice"
            : "Transfer Document List"
        }
      >
        {documentType === "SALESRETURN" || documentType === "PURCHASERETURN" ? (
          <TransferInvoiceListModal
            setTransferInvNo={setTransferInvNo}
            onPickItems={onPickItems}
            initialSearchQuery={transferInvNo}
            setIsModalOpen={setIsModalOpen}
            setInvoiceData={setInvoiceData}
            documentType={documentType}
          />
        ) : (
          <>
            <TransferOrderModal
              handleSelectAll={handleSelectAll}
              handleSelectOrder={handleSelectOrder}
              orders={modalData}
              loading={loadingModalData}
              selectedOrders={selectedOrders}
              handleTransfer={handleTransferAction}
              selectedDocType={selectedDocType}
              searchTerm={modalSearchTerm}
              setSearchTerm={setModalSearchTerm}
              fetchModalData={fetchModalData}
              loadingModalData={loadingModalData}
              setSelectedOrders={setSelectedOrders}
              getOrderId={getOrderId}
            />
          </>
        )}
      </Modal>

      <Modal
        isOpen={showExcelModal}
        onClose={() => {
          setShowExcelModal(false);
          setExcelData([]);
          setExcelFileName("");
        }}
        size="xl"
        header={true}
        title={"Import Items"}
      >
        <ExcelImportModal
          excelData={excelData}
          setExcelData={setExcelData}
          fileInputRef={fileInputRef}
          handleFileChange={handleFileChange}
          handleImport={handleImport}
          loadingExcel={loadingExcel}
          excelError={excelError}
          excelFileName={excelFileName}
        />
      </Modal>
    </div>
  );
};

export default ConfigCard;
